import type { JSX, ReactNode } from 'react';
import { useState } from 'react';

import { cn } from '../lib/cn';

import type { AlertTone } from './alert';

export interface DismissibleAlertProps {
  storageKey: string;
  tone?: AlertTone;
  children: ReactNode;
  className?: string;
}

const TONE_STYLES: Record<AlertTone, string> = {
  info: 'border-slate-300 bg-slate-50 text-slate-800',
  success: 'border-emerald-300 bg-emerald-50 text-emerald-900',
  warning: 'border-amber-300 bg-amber-50 text-amber-900',
  danger: 'border-red-300 bg-red-50 text-red-900',
};

/**
 * A single-line, dismissible banner, unlike the multiline `Alert`. The
 * consumer picks `storageKey`; persistence lives in `sessionStorage`, not in
 * this component's caller \u2014 so a dismissal already recorded before mount is
 * read once, synchronously, and the alert never flashes into view first.
 */
export function DismissibleAlert({
  storageKey,
  tone = 'warning',
  children,
  className,
}: DismissibleAlertProps): JSX.Element | null {
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(storageKey) === '1');

  if (dismissed) return null;

  function handleDismiss(): void {
    sessionStorage.setItem(storageKey, '1');
    setDismissed(true);
  }

  return (
    <div
      role="alert"
      className={cn(
        'flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm',
        TONE_STYLES[tone],
        className,
      )}
    >
      <div className="flex-1">{children}</div>
      <button
        type="button"
        aria-label="Cerrar aviso"
        onClick={handleDismiss}
        className="shrink-0 rounded p-1 hover:bg-black/5"
      >
        ×
      </button>
    </div>
  );
}
